import axios from "axios";
import base64 from "base64-js";

import {
  SET_IS_CAMERA_REQUIRED,
  SET_WEBCAM_STREAM,
  SET_CAMERA_STATUS,
  SET_CAMERA_ERROR,
} from "./actionType";

import {
  BASE_URL,
  handleDynamicRequestHeader,
  useDispatch,
} from "../../Common/CommonExport";

import { toast } from "react-toastify";
import { store } from "../index";

export const startCamera = () => async (dispatch) => {
  try {
    // First, check if permissions API is available and check current permission state
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "camera",
        });

        if (permissionStatus.state === "denied") {
          // Permission is explicitly denied
          const deniedError = new Error("Camera permission is denied");
          deniedError.name = "NotAllowedError";
          deniedError.permissionState = "denied";
          dispatch(setCameraError(deniedError));
          return false;
        }
      } catch (permErr) {
        // Permissions API might not support camera query in some browsers
        console.warn("Permission query not supported:", permErr);
      }
    }

    // Attempt to get camera stream
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: false,
    });

    if (stream) {
      dispatch(setCameraError(null));
      dispatch(setWebcamStream(stream));
      dispatch(setCameraStatus(true));
      dispatch(setIsCameraRequired(true));
      stream.oninactive = () => {
        dispatch(setCameraStatus(false));
        dispatch(
          setCameraError({
            name: "AbortError",
            message: "The camera is disconnected",
          }),
        );
      };
      return stream; // Camera started successfully
    }
  } catch (error) {
    // Enhance error with additional context
    const enhancedError = {
      ...error,
      name: error.name,
      message: error.message,
      timestamp: new Date().toISOString(),
    };

    dispatch(setCameraError(enhancedError));
    return false; // Camera failed to start
  }
};

export const setCameraError = (error) => ({
  type: SET_CAMERA_ERROR,
  payload: error,
});

export const stopCamera = () => async (dispatch, getState) => {
  const { webcamStream } = getState().cameraReducer;
  if (webcamStream) {
    if (webcamStream.getTracks) {
      webcamStream.getTracks().forEach((track) => track.stop());
    }
    dispatch(setWebcamStream(null));
    dispatch(setCameraStatus(false));
    dispatch(setIsCameraRequired(false));
    dispatch(setCameraError(null));
  }
};

export const setWebcamStream = (stream) => ({
  type: SET_WEBCAM_STREAM,
  payload: stream,
});

export const setCameraStatus = (status) => ({
  type: SET_CAMERA_STATUS,
  payload: status,
});

export const setIsCameraRequired = (isCameraRequired) => ({
  type: SET_IS_CAMERA_REQUIRED,
  payload: isCameraRequired,
});

export const takeSnapshot = (stream, testId, inviteId) => async (dispatch) => {
  // Validate stream before attempting to use it
  if (!stream || typeof stream.getVideoTracks !== "function") {
    console.warn("Invalid stream object provided to takeSnapshot");
    return;
  }

  const videoTracks = stream.getVideoTracks();
  if (!videoTracks || videoTracks.length === 0) {
    console.warn("No video tracks available in camera stream");
    return;
  }

  const videoTrack = videoTracks[0];
  if (!videoTrack || videoTrack.readyState !== "live") {
    console.warn("Video track is not in live state");
    return;
  }

  try {
    const imageCapture = new ImageCapture(videoTrack);
    const bitmap = await imageCapture.grabFrame();
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    context.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height);
    const snapshotData = canvas.toDataURL("image/jpeg");
    return dispatch(uploadSnapshot(snapshotData, testId, inviteId));
  } catch (error) {
    console.error("Error taking camera snapshot:", error);
  }
};

export const uploadSnapshot =
  (snapshot, testId, inviteId) => async (dispatch, getState) => {
    return new Promise(async (resolve, reject) => {
      const base64Data = snapshot?.replace(
        /^data:image\/(png|jpeg|jpg);base64,/,
        "",
      );
      const imageBytes = base64.toByteArray(base64Data);

      try {
        const state = getState();
        const questionId =
          state.currentQuestion?.data?._id ||
          state.currentQuestion?.data?.questionId;

        const fileData = {
          requirement: [
            {
              order: 0,
              extension: "jpg",
            },
          ],
          // module: "candidateWebCamSnapShots",
          module: `clientId/job_title_jobId/assessments/name_assessmentId/submission`,
          moduleType: "assessment_submission",
          clientId: state.testReducer.clientId,
          jobId: state.testReducer.jobId,
          assessmentId: testId,
          questionId: questionId,
          candidateId: inviteId,
          candidateName: state.candidateReducer.name,
          clientName: state.testReducer.company,
          assessmentName: state.testReducer.name,
        };

        const method = "POST";
        const fileUploadUrl = `${BASE_URL}/public/file`;
        const wrongFileUploadResponse = (error) => {
          if (!axios.isCancel(error) && !error.cancelled) {
            toast.error("Error in uploading file data");
          }
          reject(error);
        };

        const rightFileUploadResponse = async (fileResponse) => {
          const fileId = fileResponse.data[0].id || fileResponse.data[0]._id;
          const uploadURL = fileResponse.data[0].url;

          // Perform the actual upload to cloud storage
          if (uploadURL) {
            try {
              await axios.put(uploadURL, imageBytes, {
                headers: {
                  "Content-Type": "image/jpeg",
                },
              });
            } catch (e) {
              console.error("Cloud upload failed", e);
            }
          }

          // We resolve early with the fileId so the caller can use it immediately
          resolve(fileId);

          const method = "POST";
          let url = `${BASE_URL}/public/assessment/${testId}/upload-candidate-webCam-snapShots?candidateAssessmentId=${inviteId}`;
          if (questionId) {
            url += `&questionId=${questionId}`;
          }

          const wrongResponse = () => {
            // specific error handling if needed
            // toast.error("Error in uploading webcam snapshot image");
          };

          const rightResponse = async (data) => {
            // Success
          };

          const state = store.getState();
          const candidateProfilePicture = state.candidateReducer.image;

          await handleDynamicRequestHeader(
            method,
            url,
            {
              webCamSnapShotIds: fileId,
              webCamSnapshot: base64Data,
              candidateProfilePicture,
              questionId: questionId, // Ensure questionId is passed in body as well
            },
            null,
            wrongResponse,
            rightResponse,
          );
        };

        await handleDynamicRequestHeader(
          method,
          fileUploadUrl,
          fileData,
          null,
          wrongFileUploadResponse,
          rightFileUploadResponse,
        );
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  };
